import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useLanguage } from 'shared/context/LanguageContext';
import { cn } from 'shared/lib/utils';

interface StepNavigationProps {
  onBack?: () => void;
  onNext?: () => void;
  /** Custom label for the next button (defaults to common.next) */
  nextLabel?: string;
  isSubmitting?: boolean;
  hideBack?: boolean;
  hideNext?: boolean;
  nextDisabled?: boolean;
  /** When true, renders the Next button as a submit button (used by react-hook-form) */
  nextType?: 'button' | 'submit';
  /**
   * Overrides the outer container classes. Defaults to the in-card spacing
   * (top margin + divider). Pass e.g. `"w-full"` when the divider/spacing is
   * provided by a surrounding footer.
   */
  className?: string;
}

const StepNavigation: React.FC<StepNavigationProps> = ({
  onBack,
  onNext,
  nextLabel,
  isSubmitting,
  hideBack,
  hideNext,
  nextDisabled,
  nextType = 'button',
  className,
}) => {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const reduceMotion = useReducedMotion();
  const NextIcon = dir === 'rtl' ? ArrowLeft : ArrowRight;
  const BackIcon = dir === 'rtl' ? ArrowRight : ArrowLeft;

  const motionPress = reduceMotion ? undefined : { scale: 0.97 };
  const motionHover = reduceMotion ? undefined : { y: -1 };

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3',
        className ?? 'mt-8 pt-6 border-t border-gray-200/70',
      )}
    >
      <motion.button
        type="button"
        onClick={onBack}
        disabled={hideBack || isSubmitting}
        whileHover={!hideBack && !isSubmitting ? motionHover : undefined}
        whileTap={!hideBack && !isSubmitting ? motionPress : undefined}
        className={cn(
          'group inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium',
          'text-gray-600 hover:text-gray-900 hover:bg-white/70 hover:backdrop-blur',
          'border border-transparent hover:border-gray-200/80',
          'transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2',
          hideBack && 'invisible',
        )}
      >
        <BackIcon
          size={16}
          className={cn(
            'transition-transform duration-200',
            !reduceMotion &&
              (dir === 'rtl'
                ? 'group-hover:translate-x-0.5'
                : 'group-hover:-translate-x-0.5'),
          )}
        />
        {t('common.back')}
      </motion.button>

      {!hideNext && (
        <motion.button
          type={nextType}
          onClick={nextType === 'button' ? onNext : undefined}
          disabled={isSubmitting || nextDisabled}
          whileHover={!isSubmitting && !nextDisabled ? motionHover : undefined}
          whileTap={!isSubmitting && !nextDisabled ? motionPress : undefined}
          className={cn(
            'group relative inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm sm:text-base font-semibold text-white',
            'overflow-hidden isolate',
            'shadow-[0_10px_30px_-10px_rgba(191,0,53,0.6),0_4px_12px_-4px_rgba(191,0,53,0.45)]',
            'hover:shadow-[0_14px_38px_-10px_rgba(191,0,53,0.7),0_6px_18px_-4px_rgba(191,0,53,0.55)]',
            'transition-shadow',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-haat-red-500 focus-visible:ring-offset-2',
            'disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none',
          )}
        >
          {/* Gradient background layer */}
          <span
            aria-hidden="true"
            className="absolute inset-0 -z-10 bg-gradient-to-br from-haat-red-500 via-haat-red-600 to-haat-red-700"
          />
          {/* Sheen sweep on hover */}
          {!reduceMotion && (
            <span
              aria-hidden="true"
              className="absolute inset-0 -z-10 translate-x-[-120%] group-hover:translate-x-[120%] transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/25 to-transparent"
            />
          )}

          {isSubmitting ? (
            <>
              <span className="loading-spinner h-4 w-4 border-white/40 border-t-white" />
              <span>{t('common.loading')}</span>
            </>
          ) : (
            <>
              <span>{nextLabel || t('common.next')}</span>
              <NextIcon
                size={18}
                className={cn(
                  'transition-transform duration-200',
                  !reduceMotion &&
                    (dir === 'rtl'
                      ? 'group-hover:-translate-x-0.5'
                      : 'group-hover:translate-x-0.5'),
                )}
              />
            </>
          )}
        </motion.button>
      )}
    </div>
  );
};

export default StepNavigation;
